import { Injectable } from '@angular/core';
import { Source } from '@helpers/types/models';
import { Observable } from 'rxjs';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisConfigService,
	AlarisLanguageService,
	AlarisToasterService,
	ExtendableRefBookService,
} from '@campaign-portal/components-library';
import { map } from 'rxjs/operators';
import {
	FindInRulesRequest,
	FindInRulesResponse,
	SourcesCreateResponse,
	SourcesDeleteRequest,
	SourcesDeleteResponse,
	SourcesReadAllResponse,
	SourcesReadRequest,
	SourcesReadResponse,
	SourcesUpdateRequest,
	SourcesUpdateResponse
} from '@helpers/types/hlr';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { exist } from '@campaign-portal/namespace/common/id';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { ExportService } from '@helpers/api/export.service';
import { SourcesFieldsService } from './sources.fields.service';

@Injectable({
	providedIn: 'root',
})
export class SourcesService extends ExtendableRefBookService<Source<exist>> implements AbstractCRUDService {
	readonly entity = '';
	readonly title = '';
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly fields: SourcesFieldsService,
		private readonly exportService: ExportService,
		private readonly config: AlarisConfigService,
		private readonly toaster: AlarisToasterService,
		private readonly lService: AlarisLanguageService
	) {
		super();
	}

	override load(connectionTypes?: string[]): Observable<ReadResponse<Source<exist>[]>> {
		const requestParams: any = {
			active: 'source_name',
			direction: 'asc',
		};
		if (connectionTypes && connectionTypes.length > 0) {
			requestParams.filter = [{ key: 'source_connection_type', value: connectionTypes.join(',') }];
		}
		return this.api
			.loader<SourcesReadAllResponse>('Sources.Read', requestParams, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: !!resp.data,
						Total: resp.length,
						Data: resp.data,
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	override process(resp: ReadResponse<Source<exist>[]>): ReadResponse<Source<exist>[]> {
		this.list$.next(resp.Data);
		this.map$.next(
			this.list.reduce((result, item) => {
				result.set(item.SourceId, item);
				return result;
			}, new Map())
		);
		return resp;
	}

	read(params?: ReadRequest): Observable<ReadResponse<Source[]>> {
		let parsedParams: SourcesReadRequest | object = {
			active: 'source_name',
			direction: 'asc',
		};
		if (params) {
			parsedParams = this.prepareParams(params);
		}
		return this.api.loader<SourcesReadResponse>('Sources.Read', parsedParams, this.loading$).pipe(
			map((resp) => {
				return {
					Success: true,
					Total: resp.length,
					Data: resp.data,
				};
			})
		);
	}

	update(params: SourcesUpdateRequest): Observable<SourcesUpdateResponse | SourcesCreateResponse> {
		this.prepareParamsBeforeCreate(params);
		const method = params.SourceId === null ? 'Sources.Create' : 'Sources.Update';
		return this.api.loader<SourcesUpdateResponse>(method, params, this.loading$).pipe(
			map((resp) => {
				if (!resp.data?.source_id) {
					this.toaster.error(
						this.lService.translate('errors.unsuccessful'),
						this.lService.translate('sources.title')
					);
				}
				return resp;
			})
		);
	}

	delete(params: SourcesDeleteRequest): Observable<SourcesDeleteResponse> {
		return this.api.loader<SourcesDeleteResponse>('Sources.Delete', params, this.loading$);
	}

	export(total: number, params: RPCRequestParams): Observable<void> {
		params = {
			...params,
			Paging: { Skip: 0, Take: total },
		};
		return this.exportService.export(
			this.config.api,
			'Sources.Read',
			this.prepareParams(params),
			this.fields.headers,
			'sources'
		);
	}

	findSourceInRules(params: FindInRulesRequest): Observable<FindInRulesResponse> {
		return this.api.loader<FindInRulesResponse>('Rules.FindSourceInRules', params, this.loading$);
	}

	findSourceInCamaraRules(params:  FindInRulesRequest): Observable<FindInRulesResponse> {
		return this.api.loader<FindInRulesResponse>('CamaraRules.FindSourceInRules', params, this.loading$);
	}

	private prepareParamsBeforeCreate(params: SourcesUpdateRequest): SourcesUpdateRequest {
		for (const element in params) {
			if (element !== null && params[element] !== null) {
				if (
					element === 'Ttl' ||
					element === 'RequestTimeout' ||
					element === 'Port' ||
					element === 'Retries' ||
					element === 'StatusRequestDelay'
				) {
					params[element] = Number(params[element]);
				}
				if (
					(element === 'SenderPort' ||
						element === 'InvNumberCode' ||
						element === 'ActiveDb' ||
						element === 'StageDb') &&
					!params[element]
				) {
					params[element] = null;
				}
				if (
					element === 'MccSet' ||
					element === 'MccmncTranslation' ||
					element === 'MccmncTranslationBy' ||
					element === 'MccmncTranslationBy2' ||
					element === 'ErrorCodeTranslation' ||
					element === 'ResponseCodeCacheList'
				) {
					try {
						params[element] = JSON.parse(params[element]!);
					} catch (e) {
						params[element] = null;
					}
				}
			}
		}
		return params;
	}

	private prepareParams(params: RPCRequestParams): SourcesReadRequest {
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'source_name',
			direction: 'asc',
			filter: params.Filters
				? params.Filters.map((filter) => {
					return this.parseFilterField(filter);
				})
				: [],
		};
	}

	private parseFilterField(filter: Filter): { key: string; value: string } {
		const variable = filter.Field;
		const value = filter.Value;
		switch (variable) {
			case 'Name':
				return { key: 'source_name', value: value as string };
			case 'ConnectionType':
				return { key: 'source_connection_type', value: (value as string[]).toString() };
			case 'Type':
				return { key: 'source_provider_name', value: value as string };

			default:
				return { key: variable, value: value + '' };
		}
	}
}